import { ApplicationState } from "..";
import { FeatureEnum, LoggedUser } from "../../models";
import { PermissionEnum } from "../../models";
import { getEntitiesDataItems, getEntity } from "../entities/selectors";
import { ProfessionalEntity } from "../entities/state";

export const getMyUser = (state: ApplicationState): LoggedUser | undefined => state?.session?.user;
export const getMyProffesionalData = (state: ApplicationState): ProfessionalEntity | undefined =>
    state?.session?.user?.professionalId ? getEntity(state, 'professionals', state.session.user?.professionalId ?? '') : undefined

export const isLoggedIn = (state: ApplicationState): boolean => !!state?.session?.user;

export const isProfessional = (state: ApplicationState): boolean => !!state?.session?.user?.professionalId

export const isAdministrator = (state: ApplicationState): boolean => (getMyUser(state)?.permissions?.some(p => [
    PermissionEnum.Administrator,
].includes(p))) || false;

export const canSeeClients = (state: ApplicationState): boolean => (getMyUser(state)?.permissions?.some(p => [
    PermissionEnum.Administrator,
    PermissionEnum.ClientsAccess,
    PermissionEnum.ClientsManagement,
].includes(p))) || isProfessional(state) || false;

export const canSeePrices = (state: ApplicationState): boolean => (getMyUser(state)?.permissions?.some(p => [
    PermissionEnum.Administrator,
    PermissionEnum.PricesAccess,
    PermissionEnum.PricesManagement,
].includes(p))) && !getMyUser(state)?.permissions?.includes(PermissionEnum.DoNotShowPrices) || false;

export const canManagePrices = (state: ApplicationState): boolean => (getMyUser(state)?.permissions?.some(p => [
    PermissionEnum.Administrator,
    PermissionEnum.PricesManagement,
].includes(p))) || false;

export const canEditAppointments = (state: ApplicationState): boolean => (getMyUser(state)?.permissions?.some(p => [
    PermissionEnum.Administrator,
    PermissionEnum.AppointmentsManagement,
].includes(p))) || getEntitiesDataItems('professionals')(state).length > 1 || false;

export const canSeeAppointments = (state: ApplicationState): boolean => (getMyUser(state)?.permissions?.some(p => [
    PermissionEnum.Administrator,
    PermissionEnum.AppointmentsAccess,
    PermissionEnum.AppointmentsManagement,
].includes(p))) || getEntitiesDataItems('professionals')(state).length > 1 || false;

export const canEditSelfAppointments = (state: ApplicationState): boolean => canEditAppointments(state) || !!getMyUser(state)?.professionalId

export const canEditUsers = (state: ApplicationState): boolean => (getMyUser(state)?.permissions?.includes(PermissionEnum.Administrator) || false);

export const canEditClients = (state: ApplicationState): boolean => (getMyUser(state)?.permissions?.some(p => [
    PermissionEnum.Administrator,
    PermissionEnum.ClientsManagement,
].includes(p))) || false;

export const canEditOwnClients = (state: ApplicationState): boolean => (getMyUser(state)?.permissions?.some(p => [
    PermissionEnum.Administrator,
    PermissionEnum.ClientsManagement,
    PermissionEnum.MyClientsManagement,
].includes(p))) || false;

export const canEditProfessionals = (state: ApplicationState): boolean => (getMyUser(state)?.permissions?.some(p => [
    PermissionEnum.Administrator,
    PermissionEnum.ProfessionalsManagement,
].includes(p))) || false;

export const canSeeClientReports = (state: ApplicationState): boolean => (getMyUser(state)?.permissions?.some(p => [
    PermissionEnum.Administrator,
    PermissionEnum.ClientsReports,
].includes(p))) || false;

export const canSeeChangeLog = (state: ApplicationState): boolean => (getMyUser(state)?.permissions?.some(p => [
    PermissionEnum.Administrator,
].includes(p))) || false;

export const canSeeInvoices = (state: ApplicationState): boolean => (getMyUser(state)?.features?.includes(FeatureEnum.Invoices) &&
    getMyUser(state)?.permissions?.some(p => [
        PermissionEnum.Administrator,
        PermissionEnum.PricesAccess,
    ].includes(p))) || false;

export const canCreateInvoices = (state: ApplicationState): boolean => (getMyUser(state)?.features?.includes(FeatureEnum.Invoices) &&
    getMyUser(state)?.permissions?.some(p => [
        PermissionEnum.Administrator,
        PermissionEnum.PricesAccess,
    ].includes(p))) || false;

export const canAccessClientsChat = (state: ApplicationState): boolean => (getMyUser(state)?.permissions?.some(p => [
    PermissionEnum.Administrator,
    PermissionEnum.ClientsChat,
].includes(p))) || false;

export const isPaymentExpired = (state: ApplicationState): boolean => state?.session?.user?.isPaymentExpired === true;

export const loggedUserIsProfessional = (state: ApplicationState): boolean => !!state?.session?.user?.professionalId
export const loggedUserIsClient = (state: ApplicationState): boolean => !!state?.session?.user?.isClient

export const getPermissions = (state: ApplicationState): PermissionEnum[] => {
    const permissions = state?.session?.user?.permissions;
    return Array.isArray(permissions) ? permissions : [];
}
export const getFeatures = (state: ApplicationState): FeatureEnum[] => {
    const features = state?.session?.user?.features;
    return Array.isArray(features) ? features : [];
}
export const hasFeature = (feature: FeatureEnum) => (state: ApplicationState): boolean => {
    const features = state?.session?.user?.features;
    return Array.isArray(features) && features.includes(feature);
}
export const getLoggedUserCompanies = (state: ApplicationState): string[] => {
    // Derive company IDs from businesses field (companies field is deprecated)
    const businesses = state?.session?.user?.businesses;
    if (Array.isArray(businesses) && businesses.length > 0) {
        return businesses.map(b => b.id);
    }

    // Fallback for backward compatibility during transition
    const companies = state?.session?.user?.companies;
    return Array.isArray(companies) ? companies : [];
}
